import React, { useRef, useEffect, useState } from "react";
import { Camera, X, Check, RotateCcw } from "lucide-react";
import { makeT } from "../lib/i18n";

// Live camera capture; collects multiple pages as JPEG File objects.
export default function CameraCapture({ onDone, onClose, uiLang = "gu" }) {
  const t = makeT(uiLang);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [pages, setPages] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" }, audio: false,
        });
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (e) {
        setError(t("camError"));
      }
    })();
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((tr) => tr.stop());
    };
  }, []); // eslint-disable-line

  const capture = () => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c) return;
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((blob) => {
      const file = new File([blob], `page-${pages.length + 1}.jpg`, { type: "image/jpeg" });
      setPages((p) => [...p, file]);
    }, "image/jpeg", 0.92);
  };

  const finish = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    onDone(pages);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4" data-testid="camera-modal">
      <button onClick={onClose} className="absolute top-4 right-4 text-white bg-[#DC2626] yb-tactile p-3" data-testid="camera-close" aria-label="બંધ કરો">
        <X className="w-7 h-7" />
      </button>
      {error ? (
        <p className="text-white text-xl text-center max-w-sm">{error}</p>
      ) : (
        <>
          <div className="relative rounded-2xl overflow-hidden border-4 border-[#FFD600] max-w-full">
            <video ref={videoRef} autoPlay playsInline className="max-h-[55vh] max-w-full" />
          </div>
          <canvas ref={canvasRef} className="hidden" />
          <p className="text-white text-lg mt-3">{t("camPages", pages.length)}</p>
          <div className="flex items-center gap-4 mt-4">
            <button onClick={capture} data-testid="camera-capture" className="yb-tactile bg-[#FFD600] text-[#1A1C19] px-6 py-5 font-black text-xl flex items-center gap-2">
              <Camera className="w-7 h-7" /> {t("camTakePhoto")}
            </button>
            {pages.length > 0 && (
              <>
                <button onClick={() => setPages((p) => p.slice(0, -1))} className="yb-tactile bg-white px-4 py-5" aria-label="undo">
                  <RotateCcw className="w-6 h-6" />
                </button>
                <button onClick={finish} data-testid="camera-done" className="yb-tactile bg-[#15803D] text-white px-6 py-5 font-black text-xl flex items-center gap-2">
                  <Check className="w-7 h-7" /> {t("camRead")}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
